<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="start" class="mb-4">
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <report-meals-select-companies />
      </v-col>
      <v-col cols="4" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <v-data-table
      :items="customSearch(filteredMeals, headers, filters.search)"
      :loading="loading"
      :headers="getMobileHeaders(headers)"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
    >
      <template v-slot:item.name="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ item.name }}
            </span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>
      <template v-slot:body.append>
        <tr>
          <td v-if="!device.isMobile"></td>
          <td style="font-weight: bold">{{ $t("total") }}</td>
          <td v-if="selectedShifts.includes(1)" style="font-weight: bold">
            {{ totalShiftMeals("shift_one") }}
          </td>
          <td v-if="selectedShifts.includes(2)" style="font-weight: bold">
            {{ totalShiftMeals("shift_two") }}
          </td>
          <td v-if="selectedShifts.includes(3)" style="font-weight: bold">
            {{ totalShiftMeals("shift_three") }}
          </td>
          <td v-if="selectedShifts.includes(4)" style="font-weight: bold">
            {{ totalShiftMeals("shift_four") }}
          </td>
          <td style="font-weight: bold">
            {{ totalShiftMeals("total") }}
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsSelectCompanies from "@/components/report/more-restaurants/fields/ReportMealsSelectCompaniesField.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";

export default {
  name: "ReportMealsTabMeals",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    ReportMealsSelectCompanies,
  },
  data: () => ({
    meals: [],
    loading: false,
    selectedShifts: [1, 2, 3], // Po default tri smene
  }),
  methods: {
    async loadMealsForRestaurantCompanies() {
      if (!this.selectedCompanies || this.selectedCompanies.length === 0) {
        this.meals = [];
        return;
      }
      this.loading = true;
      this.meals = []; // Clear previous meals

      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
        company: this.selectedCompanies.map((company) => company.id).join(","),
      };

      try {
        const data = await reportService.getPackagesForMoreRestaurant(
          this.$store.state.restaurant.id,
          params
        );

        const aggregatedMeals = {};

        // data is an array of companies
        const companiesArray = Array.isArray(data) ? data : [];

        companiesArray.forEach((company) => {
          if (!company.meals || !Array.isArray(company.meals)) return;

          company.meals.forEach((meal) => {
            // Use name as key when id is null, otherwise use id
            const mealKey =
              meal.id !== null && meal.id !== undefined
                ? `id_${meal.id}`
                : `name_${meal.name}`;

            if (!aggregatedMeals[mealKey]) {
              aggregatedMeals[mealKey] = {
                id: meal.id,
                name: meal.name,
                shift_one: 0,
                shift_two: 0,
                shift_three: 0,
                shift_four: 0,
                total: 0,
              };
            }

            // Aggregate quantities from all shifts across companies
            aggregatedMeals[mealKey].shift_one +=
              (meal.shift_one?.customerQuantity || 0) +
              (meal.shift_one?.restaurantQuantity || 0);
            aggregatedMeals[mealKey].shift_two +=
              (meal.shift_two?.customerQuantity || 0) +
              (meal.shift_two?.restaurantQuantity || 0);
            aggregatedMeals[mealKey].shift_three +=
              (meal.shift_three?.customerQuantity || 0) +
              (meal.shift_three?.restaurantQuantity || 0);
            aggregatedMeals[mealKey].shift_four +=
              (meal.shift_four?.customerQuantity || 0) +
              (meal.shift_four?.restaurantQuantity || 0);
            aggregatedMeals[mealKey].total += meal.total || 0;
          });
        });

        this.meals = Object.values(aggregatedMeals).map((meal, index) => ({
          ...meal,
          num: index + 1,
        }));
      } catch (error) {
        this.meals = [];
        this.showErrorSnackbar(error, this.$t("error_loading_meals"));
      } finally {
        this.loading = false;
      }
    },
    totalShiftMeals(shiftNumber) {
      return this.filteredMeals.reduce((acc, meal) => acc + meal[shiftNumber], 0);
    },
  },
  computed: {
    filteredMeals() {
      if (!this.selectedShifts || this.selectedShifts.length === 0) {
        return this.meals;
      }

      return this.meals.map((meal) => {
        const filteredMeal = { ...meal, total: 0 };

        // Reset shift values based on selected shifts
        filteredMeal.shift_one = this.selectedShifts.includes(1) ? meal.shift_one : 0;
        filteredMeal.shift_two = this.selectedShifts.includes(2) ? meal.shift_two : 0;
        filteredMeal.shift_three = this.selectedShifts.includes(3) ? meal.shift_three : 0;
        filteredMeal.shift_four = this.selectedShifts.includes(4) ? meal.shift_four : 0;

        // Recalculate total
        filteredMeal.total =
          filteredMeal.shift_one +
          filteredMeal.shift_two +
          filteredMeal.shift_three +
          filteredMeal.shift_four;

        return filteredMeal;
      });
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
      ];

      const shiftHeaders = [];

      // Dodaj header kolone samo za selektovane smene
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("1st_shift"), value: "shift_one" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("2nd_shift"), value: "shift_two" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("3rd_shift"), value: "shift_three" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("4th_shift"), value: "shift_four" });
      }

      const totalHeader = { text: this.$t("total"), value: "total" };

      return [...baseHeaders, ...shiftHeaders, totalHeader];
    },
    selectedCompanies() {
      return this.$store.state.report.selectedMealCompanies;
    },
    filters() {
      return this.$store.state.report.filters;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.loadMealsForRestaurantCompanies();
      },
      deep: true,
    },
    selectedCompanies: {
      handler() {
        this.loadMealsForRestaurantCompanies();
      },
      immediate: true,
      deep: true,
    },
    selectedShifts: {
      handler() {
        // Data se automatski filtriraju preko computed property
      },
    },
    isPrinting() {
      if (this.isPrinting) {
        // Add totals row to items for printing
        const itemsWithTotals = [...this.filteredMeals];

        // Create totals row
        const totalsRow = {
          num: "",
          name: this.$t("total"),
          shift_one: this.totalShiftMeals("shift_one"),
          shift_two: this.totalShiftMeals("shift_two"),
          shift_three: this.totalShiftMeals("shift_three"),
          shift_four: this.totalShiftMeals("shift_four"),
          total: this.totalShiftMeals("total"),
        };

        itemsWithTotals.push(totalsRow);
        const companies =
          this.$store.state.report.mealCompanies.length === this.selectedCompanies.length
            ? this.$t("for_all_companies")
            : this.selectedCompanies
                .map((company) => this.toLatin(company.company))
                .join(", ");
        this.printDocument({
          tables: [{ headers: this.headers, items: itemsWithTotals, showCaption: true }],
          title: [this.$t("report"), this.$t("meals")].join(": "),
          hideDatetime: true,
          beforeTableHtml: `<h4 style="text-align: center">${companies} ${this.subtitle}</h4>`,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const itemsForExcel = this.customSearch(
          this.filteredMeals,
          this.headers,
          this.filters.search
        );

        // Add totals row to Excel items
        const totalsRow = {
          num: "",
          name: this.$t("total"),
          shift_one: this.totalShiftMeals("shift_one"),
          shift_two: this.totalShiftMeals("shift_two"),
          shift_three: this.totalShiftMeals("shift_three"),
          shift_four: this.totalShiftMeals("shift_four"),
          total: this.totalShiftMeals("total"),
        };

        itemsForExcel.push(totalsRow);

        // Compose date range for subtitle
        const from = this.filters.dates.from;
        const to = this.filters.dates.to;
        const subtitle =
          from && to
            ? `May ${from.slice(8, 10)}, ${from.slice(0, 4)} - May ${to.slice(
                8,
                10
              )}, ${to.slice(0, 4)}.`
            : "";
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("meals")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("meals"),
              headers: this.headers,
              items: itemsForExcel,
              title: this.$t("report") + " " + this.$t("meals"),
              subtitle,
            },
            {
              name: this.$t("companies"),
              headers: [
                { text: this.$t("id"), value: "id" },
                { text: this.$t("company"), value: "company" },
              ],
              items: this.selectedCompanies,
            },
          ],
        });
      }
    },
  },
};
</script>
